import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useConsol, consolChildParty } from '../../hooks/useConsol'
import { supabase } from '../../api/supabase'
import { AddChildDialog, LinkJobDialog, UnlinkDialog } from './ConsolDialogs'
import type { LinkableJob } from '../../lib/jobSearch'
import type { ConsolChild } from '../../hooks/useConsol'
import { StickyHScrollbar } from '../StickyHScrollbar'
import { useStickyHScroll } from '../../hooks/useStickyHScroll'
import { BOXTOP_SYNC_ENABLED } from '../../lib/boxtopSync'

interface ConsolTabProps {
  jobData: any
  readOnly?: boolean
}

export default function ConsolTab({ jobData, readOnly = false }: ConsolTabProps) {
  const navigate = useNavigate()
  const { fetchConsolChildren, addConsolChild, linkJobToConsol, unlinkJobFromConsol, loading } = useConsol()
  const [consolData, setConsolData] = useState<ConsolChild[] | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [loadingData, setLoadingData] = useState(true)
  // Z93b: which children have actually been pushed to BoxTop (id -> synced)
  const [syncById, setSyncById] = useState<Record<string, boolean>>({})
  const [error, setError] = useState<string | null>(null)
  // Z93: add / link / unlink dialogs. `dialogError` is shown INSIDE the open dialog so a failure is
  // never silent; `notice` is the success/partial-success banner after an action completes.
  const [dialog, setDialog] = useState<'add' | 'link' | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ kind: 'success' | 'warn'; text: string } | null>(null)
  const [unlinkTarget, setUnlinkTarget] = useState<{ child: ConsolChild; boxtrax_synced: boolean; boxtrax_ref: string | null } | null>(null)
  const { scrollProps, contentRef, bar } = useStickyHScroll([consolData])

  useEffect(() => {
    if (jobData?.id && jobData?.job_type) {
      loadConsolData()
    }
  }, [jobData?.id, jobData?.job_type])

  const loadConsolData = async () => {
    try {
      setLoadingData(true)
      setError(null)

      if (jobData.job_type === 'CONSOL') {
        // Load child HBL jobs for master consolidation
        const children = await fetchConsolChildren(jobData.id)
        setConsolData(Array.isArray(children) ? children : null)
        const ids = (Array.isArray(children) ? children : []).map(c => c.id)
        if (ids.length > 0) {
          const { data: syncRows } = await supabase.from('jobs').select('id, boxtrax_synced').in('id', ids)
          setSyncById(Object.fromEntries((syncRows || []).map((r: { id: string; boxtrax_synced: boolean | null }) => [r.id, !!r.boxtrax_synced])))
        } else {
          setSyncById({})
        }
      }
    } catch (err) {
      console.error('Error loading consol data:', err)
      setError('Failed to load consolidation data')
    } finally {
      setLoadingData(false)
    }
  }

  // Z93: Unlink first looks up whether this child was already synced to BoxTop, because the
  // confirmation has to say what will (and won't) happen there.
  const handleUnlink = async (child: ConsolChild) => {
    setDialogError(null)
    const { data, error: lookupErr } = await supabase.from('jobs').select('boxtrax_synced, boxtrax_ref').eq('id', child.id).maybeSingle()
    if (lookupErr || !data) {
      setError(`Could not check ${child.job_ref}'s BoxTop status${lookupErr ? `: ${lookupErr.message}` : ''}. Nothing was changed.`)
      return
    }
    const row = data as { boxtrax_synced: boolean | null; boxtrax_ref: string | null }
    setUnlinkTarget({ child, boxtrax_synced: !!row.boxtrax_synced, boxtrax_ref: row.boxtrax_ref ?? null })
  }

  const confirmUnlink = async () => {
    if (!unlinkTarget) return
    const { child } = unlinkTarget
    const boxtrax_synced = BOXTOP_SYNC_ENABLED && unlinkTarget.boxtrax_synced
    const res = await unlinkJobFromConsol(jobData.id, child.id)
    if (!res.ok) { setDialogError(res.error); return }
    setUnlinkTarget(null)
    setSelectedIds(prev => { const n = new Set(prev); n.delete(child.id); return n })
    setNotice({
      kind: boxtrax_synced ? 'warn' : 'success',
      text: boxtrax_synced
        ? `${child.job_ref} was unlinked. It was already synced to BoxTop, and that record is unchanged — fix it in BoxTop separately if needed.`
        : `${child.job_ref} was unlinked and is now an independent job.`,
    })
    await loadConsolData()
  }

  const handleAdd = async (v: { houseBlNo: string; importerId: string; consignorId: string; containerNo: string }) => {
    const res = await addConsolChild(jobData.id, v)
    if (!res.ok) { setDialogError(res.error); return }
    setDialog(null)
    setNotice({ kind: 'success', text: `Created ${res.data.job_ref}. It is pending BoxTop sync — open it and press Sync when ready.` })
    await loadConsolData()
  }

  const handleLink = async (job: LinkableJob) => {
    const res = await linkJobToConsol(jobData.id, job.id)
    if (!res.ok) { setDialogError(res.error); return }
    setDialog(null)
    setNotice({
      kind: BOXTOP_SYNC_ENABLED && res.data.boxtrax_synced ? 'warn' : 'success',
      text: BOXTOP_SYNC_ENABLED && res.data.boxtrax_synced
        ? `${res.data.job_ref} is now linked (reference unchanged). It was already synced to BoxTop and that record is unchanged.`
        : `${res.data.job_ref} is now linked (reference unchanged).`,
    })
    await loadConsolData()
  }

  const toggleSelect = (childId: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(childId)) {
        newSet.delete(childId)
      } else {
        newSet.add(childId)
      }
      return newSet
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === consolData?.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(consolData?.map(c => c.id) || []))
    }
  }

  const renderAddressLines = (data: any, prefix: string) => {
    const lines: string[] = []

    const line1 = data[`${prefix}_addr_l1`]?.trim() || ''
    const line2 = data[`${prefix}_addr_l2`]?.trim() || ''
    const city = data[`${prefix}_city`]?.trim() || ''
    const postcode = data[`${prefix}_postcode`]?.trim() || ''
    const country = data[`${prefix}_country`]?.trim() || ''

    // Add address line 1
    if (line1) lines.push(line1)

    // Add address line 2 if different from line 1
    if (line2 && line2 !== line1) lines.push(line2)

    // Add city and postcode if they exist and are different from previous lines
    const cityPostcode = [city, postcode].filter(Boolean).join(', ')
    if (cityPostcode && cityPostcode !== line1 && cityPostcode !== line2) {
      lines.push(cityPostcode)
    }

    // Add country if it exists and is different from city and not already added
    if (country && country !== city && !lines.includes(country)) {
      lines.push(country)
    }

    return lines
  }

  if (loadingData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 px-4 py-3 rounded">
        {error}
      </div>
    )
  }

  if (jobData.job_type !== 'CONSOL') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Consol tab is only available for master consolidations</p>
      </div>
    )
  }

  const children = consolData || []

  const actionButtons = (
    <>
      <button
        onClick={() => { setDialogError(null); setDialog('add') }}
        className="px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
      >
        + Add child job
      </button>
      <button
        onClick={() => { setDialogError(null); setDialog('link') }}
        className="px-3 py-1 text-sm rounded border border-blue-600 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
      >
        Link existing job
      </button>
    </>
  )
  const nextIndex = children.reduce((m, c) => Math.max(m, c.job_ref_index || 0), 0) + 1

  // Calculate totals
  const sumPacks = children.reduce((sum, c) => sum + (c.total_packs || 0), 0)
  const sumGross = children.reduce((sum, c) => sum + (c.total_gross_weight || 0), 0)
  const sumCharg = children.reduce((sum, c) => sum + (c.total_charg_weight || 0), 0)
  const sumVolume = children.reduce((sum, c) => sum + (c.total_volume || 0), 0)

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              🏠 Child HBL Jobs ({children.length})
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              House bills of lading linked to this master consolidation
            </p>
            {Object.values(syncById).some(v => v === false) && (
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                {Object.values(syncById).filter(v => v === false).length} of {children.length} not yet synced to BoxTop — open each job and press Sync.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!readOnly && actionButtons}
            <button
              onClick={loadConsolData}
              className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded"
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {notice && (
          <div className={`mb-4 flex items-start justify-between rounded border px-4 py-3 text-sm ${notice.kind === 'warn'
            ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
            : 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300'}`}>
            <span>{notice.text}</span>
            <button className="ml-3 text-xs underline" onClick={() => setNotice(null)}>Dismiss</button>
          </div>
        )}

        {children.length > 0 ? (
          <>
            {/* Table - Desktop view */}
            <div className="hidden lg:block overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg" {...scrollProps}>
              <table ref={contentRef as React.RefObject<HTMLTableElement>} className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="px-3 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === children.length && children.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </th>
                    <th className="px-3 py-3 text-left font-medium text-gray-900 dark:text-white">Open</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-900 dark:text-white">FileRef</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-900 dark:text-white">JobRef</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-900 dark:text-white">House</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-900 dark:text-white">Container</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-900 dark:text-white">Consignor</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-900 dark:text-white">Importer</th>
                    <th className="px-3 py-3 text-right font-medium text-gray-900 dark:text-white">No Pcs</th>
                    <th className="px-3 py-3 text-right font-medium text-gray-900 dark:text-white">Gross Wt</th>
                    <th className="px-3 py-3 text-right font-medium text-gray-900 dark:text-white">Charg Wt</th>
                    <th className="px-3 py-3 text-right font-medium text-gray-900 dark:text-white">Cube</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-900 dark:text-white">Goods Description</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-900 dark:text-white">Unlink</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                  {children.map((child) => (
                    <tr key={child.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(child.id)}
                          onChange={() => toggleSelect(child.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <button
                          onClick={() => navigate(`/jobs/${child.id}`)}
                          className="text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20 px-2 py-1 rounded text-xs font-medium"
                        >
                          Open →
                        </button>
                      </td>
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-300">
                        {child.file_ref || '-'}
                      </td>
                      <td className="px-3 py-3">
                        <Link
                          to={`/jobs/${child.id}`}
                          className="font-mono font-bold text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {child.job_ref}
                        </Link>
                        {syncById[child.id] === false && (
                          <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">Not synced to BoxTop</span>
                        )}
                        {syncById[child.id] === true && (
                          <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">Synced</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-300 font-mono">
                        {child.house_bl_no || '-'}
                      </td>
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-300 font-mono">
                        {child.container_numbers || '-'}
                      </td>
                      {/* Consignor column (swapped to come first) */}
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-300 text-xs max-w-xs">
                        {consolChildParty(child, 'consignor').name ? (
                          <>
                            <div className="font-medium">{consolChildParty(child, 'consignor').name}</div>
                            {renderAddressLines(child, consolChildParty(child, 'consignor').prefix).map((line, i) => (
                              <div key={i} className="text-gray-600 dark:text-gray-400">{line}</div>
                            ))}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      {/* Importer column */}
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-300 text-xs max-w-xs">
                        {consolChildParty(child, 'importer').name ? (
                          <>
                            <div className="font-medium">{consolChildParty(child, 'importer').name}</div>
                            {renderAddressLines(child, consolChildParty(child, 'importer').prefix).map((line, i) => (
                              <div key={i} className="text-gray-600 dark:text-gray-400">{line}</div>
                            ))}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700 dark:text-gray-300">
                        {child.total_packs || 0}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700 dark:text-gray-300 font-mono">
                        {child.total_gross_weight ? child.total_gross_weight.toFixed(2) : '0.00'}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700 dark:text-gray-300 font-mono">
                        {child.total_charg_weight ? child.total_charg_weight.toFixed(2) : '0.00'}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700 dark:text-gray-300 font-mono">
                        {child.total_volume ? child.total_volume.toFixed(4) : '0.0000'}
                      </td>
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-300 text-xs max-w-sm">
                        {child.goods_description || '-'}
                      </td>
                      <td className="px-3 py-3">
                        {!readOnly && (
                          <button
                            onClick={() => handleUnlink(child)}
                            disabled={loading}
                            className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-50"
                          >
                            Unlink
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <StickyHScrollbar {...bar} />

            {/* Summary row */}
            <div className="border-t mt-4 pt-4 grid grid-cols-2 gap-6 text-sm bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Total Packs:</span>
                <span className="ml-2 font-bold text-gray-900 dark:text-white">{sumPacks}</span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Total Gross Wt:</span>
                <span className="ml-2 font-bold text-gray-900 dark:text-white font-mono">{sumGross.toFixed(2)} KG</span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Total Charg Wt:</span>
                <span className="ml-2 font-bold text-gray-900 dark:text-white font-mono">{sumCharg.toFixed(2)} KG</span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Total Volume:</span>
                <span className="ml-2 font-bold text-gray-900 dark:text-white font-mono">{sumVolume.toFixed(4)} m³</span>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-gray-500 dark:text-gray-400">No child HBL jobs on this master yet</p>
            {!readOnly && (
              <>
                <p className="mt-1 mb-4 text-sm text-gray-500 dark:text-gray-400">Add a new house job, or link one that already exists.</p>
                <div className="flex justify-center gap-2">{actionButtons}</div>
              </>
            )}
          </div>
        )}

      </div>

      {dialog === 'add' && (
        <AddChildDialog
          master={jobData}
          nextRef={`${jobData.job_ref}-${String(nextIndex).padStart(2, '0')}`}
          busy={loading}
          error={dialogError}
          onSubmit={handleAdd}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'link' && (
        <LinkJobDialog master={jobData} busy={loading} error={dialogError} onLink={handleLink} onClose={() => setDialog(null)} />
      )}
      {unlinkTarget && (
        <UnlinkDialog
          master={jobData}
          child={{ ...unlinkTarget.child, boxtrax_synced: unlinkTarget.boxtrax_synced, boxtrax_ref: unlinkTarget.boxtrax_ref }}
          busy={loading}
          error={dialogError}
          onConfirm={confirmUnlink}
          onClose={() => setUnlinkTarget(null)}
        />
      )}
    </div>
  )
}
